import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { useConfirmDialog } from "contexts/confirmDialog";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { sorted } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { useUserUid } from "hooks/settings";
import { useHotkeys } from "react-hotkeys-hook";
import { ReviewPeriodData } from "src/store/api/generatedApi";
import {
  calculateFocusedWindow,
  canMoveBackward,
  canMoveForward,
  clampWindowToDay,
} from "../utils/timeUtils";
import { MANUAL_REVIEW_SCOPE } from "../hotkeyScopes";

dayjs.extend(utc);

export const DEFAULT_WINDOW_MINUTES = 30;

const sortedUnverifiedPeriods = (reviewPeriods: ReviewPeriodData[]) => {
  return sorted(
    reviewPeriods.filter((period) => period.verified_at == null),
    (period) => period.start
  );
};

export const useManualReviewNavigation = ({
  dayStart,
  dayEnd,
  reviewPeriods,
  focusTimestamp,
  isDirtyRef,
}: {
  dayStart: Dayjs;
  dayEnd: Dayjs;
  reviewPeriods: ReviewPeriodData[];
  focusTimestamp: Dayjs | null;
  isDirtyRef: RefObject<boolean>;
}) => {
  const { getHotkey } = useHotkeyContext();
  const { t } = useTenantTranslation();
  const confirmSkip = useConfirmDialog();
  const isConfirmingRef = useRef(false);

  const [viewingWindowStart, setViewingWindowStart] = useState(dayStart);

  const userUid = useUserUid();

  const [viewingWindowEnd, setViewingWindowEnd] = useState<Dayjs>(() =>
    dayStart.add(DEFAULT_WINDOW_MINUTES, "minutes")
  );

  const focusAppliedRef = useRef(false);

  const clampWindow = useCallback(
    (start: Dayjs, end: Dayjs) => {
      return clampWindowToDay(start, end, dayStart, dayEnd);
    },
    [dayStart, dayEnd]
  );

  useEffect(() => {
    if (focusTimestamp && !focusAppliedRef.current) {
      // Centre the window on the deep-linked moment, snapped to the review-period
      // grid so the window never straddles a period boundary.
      const { start: focusedStart, end: focusedEnd } = calculateFocusedWindow(
        focusTimestamp,
        dayStart,
        dayEnd,
        DEFAULT_WINDOW_MINUTES
      );
      setViewingWindowStart(focusedStart);
      setViewingWindowEnd(focusedEnd);
      focusAppliedRef.current = true;
    } else if (!focusAppliedRef.current) {
      setViewingWindowStart(dayStart);
      setViewingWindowEnd(dayStart.add(DEFAULT_WINDOW_MINUTES, "minutes"));
    }
  }, [dayStart, dayEnd, focusTimestamp]);

  const goToPeriod = useCallback(
    (
      period: ReviewPeriodData,
      alignTo: "start" | "end" | undefined = "start"
    ) => {
      const periodStart = dayjs.utc(period.start);
      const periodEnd = dayjs.utc(period.end);
      const periodDuration = periodEnd.diff(periodStart, "minutes");
      const windowStart =
        alignTo === "start"
          ? periodStart
          : periodEnd.subtract(DEFAULT_WINDOW_MINUTES, "minutes");
      const windowEnd =
        alignTo === "start"
          ? periodEnd.add(DEFAULT_WINDOW_MINUTES - periodDuration, "minutes")
          : periodEnd;
      const { start: clampedStart, end: clampedEnd } = clampWindow(
        windowStart,
        windowEnd
      );
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- called from useEffect to navigate to a period
      setViewingWindowStart(clampedStart);
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- called from useEffect to navigate to a period
      setViewingWindowEnd(clampedEnd);
    },
    [clampWindow]
  );

  useEffect(() => {
    if (focusTimestamp) return;

    const userAssignedUnverifiedPeriod = reviewPeriods.find(
      (period) =>
        period.assigned_to_id === userUid && period.verified_at == null
    );
    if (userAssignedUnverifiedPeriod) {
      goToPeriod(userAssignedUnverifiedPeriod);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- intentionally omits reviewPeriods; only runs on mount to avoid re-running when periods load
  }, [userUid, goToPeriod, focusTimestamp]);

  const guardSkip = useCallback(
    async (navigate: () => void, alsoDirty = false) => {
      if (!isDirtyRef.current && !alsoDirty) {
        navigate();
        return;
      }
      if (isConfirmingRef.current) return;
      isConfirmingRef.current = true;
      try {
        const confirmed = await confirmSkip({
          title: t("confirm"),
          message: t("discardEditsWarning"),
          confirmOnEnter: true,
        });
        if (confirmed) navigate();
      } finally {
        isConfirmingRef.current = false;
      }
    },
    [confirmSkip, t, isDirtyRef]
  );

  const goToNextPeriod = useCallback(() => {
    if (!reviewPeriods.length) return;

    const sortedPeriods = sortedUnverifiedPeriods(reviewPeriods);
    const nextPeriod = sortedPeriods.find((period) =>
      dayjs(period.start).isAfter(viewingWindowEnd)
    );

    if (!nextPeriod) return;
    void guardSkip(() => {
      goToPeriod(nextPeriod);
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- uses viewingWindowEnd in body but depends on viewingWindowStart so the callback updates when the window position changes; viewingWindowEnd tracks alongside it
  }, [reviewPeriods, viewingWindowStart, guardSkip, goToPeriod]);

  const goToPreviousPeriod = useCallback(() => {
    if (!reviewPeriods.length) return;

    const sortedPeriods = sortedUnverifiedPeriods(reviewPeriods).reverse();
    const prevPeriod = sortedPeriods.find((period) =>
      dayjs(period.start).isBefore(viewingWindowStart)
    );

    if (!prevPeriod) return;
    void guardSkip(() => {
      goToPeriod(prevPeriod, "end");
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- goToPeriod depends on clampWindow which changes with dayStart/dayEnd; including it would cause unnecessary re-creation of this callback
  }, [reviewPeriods, viewingWindowStart, guardSkip]);

  const moveBackOneHour = useCallback(() => {
    if (!canMoveBackward(viewingWindowStart, dayStart)) {
      return;
    }

    void guardSkip(() => {
      const newEnd = viewingWindowStart;
      const newStart = newEnd.subtract(DEFAULT_WINDOW_MINUTES, "minutes");

      const { start: clampedStart } = clampWindow(newStart, newEnd);

      setViewingWindowStart(clampedStart);
      setViewingWindowEnd(newEnd);
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- clampWindow and dayStart are stable for a given day; including them would cause unnecessary re-creation when only viewingWindowStart matters
  }, [viewingWindowStart, guardSkip]);

  const moveForwardOneHour = useCallback(() => {
    if (!canMoveForward(viewingWindowEnd, dayEnd)) {
      return;
    }

    void guardSkip(() => {
      const newStart = viewingWindowEnd;
      const newEnd = newStart.add(DEFAULT_WINDOW_MINUTES, "minutes");

      const { end: clampedEnd } = clampWindow(newStart, newEnd);

      setViewingWindowStart(newStart);
      setViewingWindowEnd(clampedEnd);
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- clampWindow and dayEnd are stable for a given day; including them would cause unnecessary re-creation when only viewingWindowEnd matters
  }, [viewingWindowEnd, guardSkip]);

  const setViewingWindow = useCallback((start: Dayjs, end: Dayjs) => {
    setViewingWindowStart(start);
    setViewingWindowEnd(end);
  }, []);

  const resetToOneHour = useCallback((start: Dayjs) => {
    setViewingWindowStart(start);
    setViewingWindowEnd(start.add(DEFAULT_WINDOW_MINUTES, "minutes"));
  }, []);

  const nextItemKey = getHotkey("nextItem");
  const previousItemKey = getHotkey("previousItem");
  const jumpBackKey = getHotkey("jumpBack");
  const jumpForwardKey = getHotkey("jumpForward");

  useHotkeys(
    nextItemKey,
    goToNextPeriod,
    { enableOnFormTags: false, scopes: [MANUAL_REVIEW_SCOPE] },
    [nextItemKey, goToNextPeriod]
  );
  useHotkeys(
    previousItemKey,
    goToPreviousPeriod,
    { enableOnFormTags: false, scopes: [MANUAL_REVIEW_SCOPE] },
    [previousItemKey, goToPreviousPeriod]
  );
  useHotkeys(
    jumpBackKey,
    moveBackOneHour,
    { enableOnFormTags: false, scopes: [MANUAL_REVIEW_SCOPE] },
    [jumpBackKey, moveBackOneHour]
  );
  useHotkeys(
    jumpForwardKey,
    moveForwardOneHour,
    { enableOnFormTags: false, scopes: [MANUAL_REVIEW_SCOPE] },
    [jumpForwardKey, moveForwardOneHour]
  );

  return {
    viewingWindowStart,
    viewingWindowEnd,
    guardSkip,
    goToNextPeriod,
    goToPreviousPeriod,
    moveBackOneHour,
    moveForwardOneHour,
    setViewingWindow,
    resetToOneHour,
    goToPeriod,
  };
};
